import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Session } from "@supabase/supabase-js";
import i18n, { isSupportedLang } from "./lib/i18n";
import { supabaseV2 } from "./lib/supabase";
import { FullScreenState } from "./components/state";
import { PasswordResetScreen } from "./features/auth/PasswordResetScreen";
import { AuthScreen } from "./features/auth/AuthScreen";
import { PublicResponsePage } from "./features/public/PublicResponsePage";
import { JoinTeamPage } from "./features/public/JoinTeamPage";
import { AuthenticatedApp } from "./app/AuthenticatedApp";

const AUTH_PATHS = new Set(["/login", "/signup", "/recovery", "/resend-confirmation"]);
const RESET_PATH = "/reset-password";
const JOIN_PATH_PREFIX = "/join/";

function joinTokenFromPath(pathname: string): string | null {
  if (!pathname.startsWith(JOIN_PATH_PREFIX)) return null;
  const token = pathname.slice(JOIN_PATH_PREFIX.length).split("/")[0] || "";
  return token.trim() || null;
}

function App() {
  const { t, i18n: i18nInst } = useTranslation("common");
  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Default <title> del tab del browser per gli stati pre-autenticazione
  // (login, signup, reset, join, response pubblica). AuthenticatedApp
  // sovrascrive con un title dinamico per-view appena monta. Senza questo
  // hook il browser tab resta con la stringa italiana hardcoded di index-v2.html
  // anche per utenti EN/DE.
  useEffect(() => {
    if (session) return;
    document.title = "ElevenBase";
  }, [session, i18nInst.language]);
  const publicToken = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("t")
    : null;
  const joinToken = joinTokenFromPath(location.pathname);

  useEffect(() => {
    supabaseV2.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setAuthLoading(false);
    });
    const { data } = supabaseV2.auth.onAuthStateChange((event, nextSession) => {
      if (event === "PASSWORD_RECOVERY") {
        setPasswordRecovery(true);
        navigate(RESET_PATH, { replace: true });
      }
      setSession(nextSession);
    });
    return () => data.subscription.unsubscribe();
  }, [navigate]);

  // Sync della lingua dal raw_user_meta_data SOLO al primo login del browser
  // corrente (cioè quando localStorage `eb.lang` è vuoto). Una volta che
  // l'utente ha fatto una scelta in questo browser (via LangSwitcher), quella
  // scelta è autoritativa: i refresh JWT successivi non la sovrascrivono.
  // Il sync cross-device resta efficace: un browser nuovo senza localStorage
  // adotta la lingua salvata su user_metadata.
  const langSyncedRef = useRef(false);
  useEffect(() => {
    if (!session || langSyncedRef.current) return;
    langSyncedRef.current = true;
    if (typeof window !== "undefined" && window.localStorage.getItem("eb.lang")) return;
    const lang = session.user.user_metadata?.lang;
    if (isSupportedLang(lang) && i18n.language?.split("-")[0] !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [session]);

  // Auto-consume pending invite token: copre il caso in cui l'utente fa signup
  // via /join/<token>, conferma la mail su un device/browser diverso e atterra
  // sull'app loggato ma senza che `consume_team_invite` sia mai stato chiamato
  // (Davide Botta docet, 2026-05-17). Idempotente: se la membership esiste
  // gia' il check la salta. Best-effort, errori loggati ma non bloccanti.
  const consumedInviteRef = useRef(false);
  useEffect(() => {
    if (!session || consumedInviteRef.current) return;
    const meta = session.user.user_metadata as Record<string, any> | undefined;
    const token = meta?.invite_token as string | undefined;
    const metaTeamId = meta?.team_id as string | undefined;
    if (!token || !metaTeamId) return;
    consumedInviteRef.current = true;
    (async () => {
      try {
        const { data: existing } = await supabaseV2
          .from("team_memberships")
          .select("id")
          .eq("team_id", metaTeamId)
          .eq("user_id", session.user.id)
          .eq("status", "active")
          .maybeSingle();
        if (existing) return; // gia' dentro, niente da fare
        const { error } = await supabaseV2.rpc("consume_team_invite", { _token: token });
        if (error) {
          console.warn("[auto-consume-invite] failed:", error.message);
        }
      } catch (e) {
        console.warn("[auto-consume-invite] exception:", e);
      }
    })();
  }, [session]);

  if (publicToken) return <PublicResponsePage token={publicToken} />;
  if (joinToken) return <JoinTeamPage token={joinToken} />;
  if (authLoading) return <FullScreenState title={t("common.loadingTitle")} description={t("common.loadingSession")} />;
  if (passwordRecovery && session) {
    return <PasswordResetScreen onDone={() => {
      setPasswordRecovery(false);
      navigate("/home", { replace: true });
    }} />;
  }
  if (!session) {
    // Se l'utente non è loggato e atterra su un path applicativo, lo portiamo a /login.
    if (!AUTH_PATHS.has(location.pathname) && location.pathname !== RESET_PATH) {
      // Lasciamo a AuthScreen il default mode = login senza forzare redirect (UX più gentile).
    }
    return <AuthScreen />;
  }
  return <AuthenticatedApp session={session} />;
}

export default App;
